import { capSQLiteSet } from "@capacitor-community/sqlite";
import SQLDatabase, {
  SQLColumn,
  SQLQueryBuilder,
  SQLSelect,
} from "../sql.service";

export type AppDatabaseTable =
  | "projects"
  | "collections"
  | "forms"
  | "formGroups"
  | "states"
  | "pages"
  | "attributes"
  | "records"
  | "users"
  | "attachments"
  | "associations"
  | "items"
  | "mediaItems";

type AppDatabaseRepoQuery = number | SQLSelect | SQLQueryBuilder;

export type CoreoAsyncIterable<T> = AsyncIterable<T[]> & {
  toArray: () => Promise<T[]>;
  offset: number;
  done: boolean;
  next: () => Promise<IteratorResult<T[]>>;
};

export abstract class AppDatabaseRepo<D, S, A> {
  abstract columns: string[][];
  abstract references: string[];
  abstract indexes: string[];
  abstract tableName: string;
  abstract primaryKey: string[];

  order: string;

  public abstract toInsertSet(entity: S): capSQLiteSet[];

  public async findById(id: number): Promise<A> {
    const query = SQLDatabase.select().from(this.tableName).where("id = ?", id);

    const result = await SQLDatabase.instance.query<D>(query);

    if (result.length === 0) {
      return null;
    }

    const serialised = result[0];
    return this.deserialise(serialised);
  }

  private buildQuery(query: AppDatabaseRepoQuery): SQLSelect {
    if (typeof query === "number") {
      return SQLDatabase.select().from(this.tableName).where("id = ?", query);
    }
    if (typeof query === "function") {
      return query(SQLDatabase.select().from(this.tableName));
    }
    return query;
  }

  public async find(query: AppDatabaseRepoQuery): Promise<A[]> {
    const result = await SQLDatabase.instance.query<D>(this.buildQuery(query));
    return Promise.all(result.map((r) => this.deserialise(r)));
  }

  public async findOne(query: AppDatabaseRepoQuery): Promise<A> {
    const q = this.buildQuery(query);
    q.limit(1);
    const result = await SQLDatabase.instance.query<D>(q);
    if (result.length === 0) {
      console.warn("findOne failed", q.toString());
      return null;
    }
    return this.deserialise(result[0]);
  }

  private queryFromPartial(query: Partial<D>): SQLSelect {
    const q = SQLDatabase.select().from(this.tableName);
    for (const p in query) {
      q.where(`${p} = ? `, query[p]);
    }
    if (this.order) {
      q.order(this.order);
    }

    return q;
  }

  public async query(query: Partial<D>): Promise<A[]> {
    const q = this.queryFromPartial(query);
    const { text, values } = q.toParam();
    const result = await SQLDatabase.instance.query<D>(text, values);
    return Promise.all(result.map((r) => this.deserialise(r)));
  }

  public async queryOne(query: Partial<D>): Promise<A> {
    const q = this.queryFromPartial(query);
    q.limit(1);

    const { text, values } = q.toParam();
    const result = await SQLDatabase.instance.query<D>(text, values);

    if (result.length === 0) {
      console.warn("queryOne failed", text, values);
      return null;
    }

    return this.deserialise(result[0]);
  }

  async count(query: AppDatabaseRepoQuery): Promise<number> {
    const q = this.buildQuery(query);
    q.field("COUNT(*)", "count");
    return SQLDatabase.instance.query<{ count: number }>(q).then((result) => {
      return result[0].count;
    });
  }

  public async initTable(): Promise<capSQLiteSet[]> {
    const existingColumns = await SQLDatabase.instance.tableColumns(
      this.tableName
    );
    if (existingColumns.length === 0) {
      return this.createTable();
    } else {
      return this.migrate(existingColumns);
    }
  }

  private createTableStatement(tableName: string = this.tableName, columns: string[][] = this.columns): capSQLiteSet {
    const statements: string[] = [];
    for (const [name, type] of columns) {
      statements.push(`"${name}" ${type}`);
    }
    for (const reference of this.references) {
      statements.push(reference);
    }
    if (this.primaryKey.length > 0) {
      const pk = `PRIMARY KEY (${this.primaryKey.map(p => `"${p}"`).join(",")})`;
      statements.push(pk);
    }
    return {
      statement: `CREATE TABLE IF NOT EXISTS ${tableName} (
        ${statements.join(",")})`,
      values: [],
    };
  }

  private createTableIndexStatement(): capSQLiteSet[] {
    return this.indexes.map((index) => ({
      statement: index,
      values: [],
    }));
  }

  public createTable(): capSQLiteSet[] {
    const operations: capSQLiteSet[] = [];
    operations.push(this.createTableStatement());
    operations.push(...this.createTableIndexStatement());
    return operations;
  }

  public async migrate(existingColumns: SQLColumn[]): Promise<capSQLiteSet[]> {
    const operations: capSQLiteSet[] = [];

    const currentPK = existingColumns.filter(c => c.pk).map(c => c.name);

    if (currentPK.length !== this.primaryKey.length) {
      console.log('PK Migration!');
      operations.push({ statement: 'BEGIN TRANSACTION', values: [] });
      const tmpTableName = `temp_${this.tableName}`;
      // The temporary table must only include columns that exist in the current table, so filter
      // out any new ones before we create it. New columns will be created in the next step.
      const tempColumns = this.columns.filter(([name]) => existingColumns.findIndex(c => c.name === name) !== -1);
      operations.push(this.createTableStatement('temp_' + this.tableName, tempColumns));
      operations.push({
        statement: `INSERT INTO ${tmpTableName} SELECT * FROM ${this.tableName}`,
        values: [],
      });
      operations.push({
        statement: `DROP TABLE ${this.tableName}`,
        values: [],
      });
      operations.push({
        statement: `ALTER TABLE ${tmpTableName} RENAME TO ${this.tableName}`,
        values: []
      });
      operations.push(...this.createTableIndexStatement());
      operations.push({ statement: 'COMMIT TRANSACTION', values: [] });
    }

    for (const [name, type] of this.columns) {
      const exists = existingColumns.findIndex((c) => c.name === name) !== -1;
      if (!exists) {
        operations.push({
          statement: `ALTER TABLE "${this.tableName}" ADD COLUMN "${name}" ${type}`,
          values: [],
        });
      }
    }
    for (const index of this.indexes) {
      operations.push({
        statement: index,
        values: [],
      });
    }
    if (this.indexes.length > 0) {
      operations.push({
        statement: `ANALYZE ${this.tableName}`,
        values: []
      });
    }

    return operations;
  }

  public toDeleteSet(id: number): capSQLiteSet[] {
    return [
      {
        statement: `DELETE FROM ${this.tableName} WHERE projectId = ?`,
        values: [id],
      },
    ];
  }

  public selectQuery() {
    return SQLDatabase.select().from(this.tableName);
  }

  public updateQuery() {
    return SQLDatabase.update().table(this.tableName);
  }

  public deleteQuery() {
    return SQLDatabase.delete().from(this.tableName);
  }

  abstract deserialise(entity: D): Promise<A>;

  public search(
    queryBuilder?: SQLQueryBuilder | Partial<D>,
    batch: number = 100
  ): CoreoAsyncIterable<A> {
    const baseQuery = SQLDatabase.select().from(this.tableName);
    let query: SQLSelect;
    if (typeof queryBuilder === 'function') {
      query = queryBuilder(baseQuery);
    } else {
      query = this.queryFromPartial(queryBuilder);
    }
    const self = this;

    const iterator: CoreoAsyncIterable<A> = {
      [Symbol.asyncIterator](): AsyncIterator<A[]> {
        return this;
      },
      offset: 0,
      done: false,
      async next() {
        if (this.done) {
          return {
            done: true,
            value: [],
          };
        }

        const qs = query.clone().limit(batch).offset(this.offset);

        const result = await SQLDatabase.instance.query<D>(qs);
        const rows = result ?? [];
        const d = await Promise.all(rows.map((row) => self.deserialise(row)));

        if (rows.length === 0) {
          this.done = true;
        } else {
          if (rows.length < batch) {
            this.done = true;
          } else {
            this.offset += batch;
          }
        }

        return {
          done: d.length === 0,
          value: d,
        };
      },

      async toArray() {
        const result = [];
        for await (const i of this) {
          result.push(...i);
        }
        return result;
      },
    };
    return iterator;
  }
}
